<script lang="ts">
	import InputField from '../../molecules/InputField.svelte'
	import { formatIsoDateString, inputFieldFormats } from '$utils/formatters'
	import TextArea from '../../atoms/TextArea.svelte'
	import SecondaryButton from '../../atoms/SecondaryButton.svelte'
	import { ButtonIcons } from '$types/enums'
	import Icon from '../../atoms/Icon.svelte'
	import closeIcon from '@/icons/icon_close.svg'
	import IconSendMail from '$assets/svg/icon-send-mail.svg?component'
	import Switch from '../../atoms/Switch.svelte'
	import {
		NewPlatformRequest,
		PFR_LAST_SEND_WITH_TYPES,
		type PlatformRequest,
		type PlatformRequestLastSend,
		PlatformRequestPopUserType,
		type PlatformRequestReceiver,
		platformRequestSchema,
		PlatformRequestSendErrorType,
		type PlatformRequestSendResponse
	} from './PlatformRequest.model'
	import Skeleton from 'svelte-skeleton/Skeleton.svelte'
	import { onMount, setContext } from 'svelte'
	import {
		getAttachments,
		getLastSend,
		getMailTemplate,
		getReceiver,
		getSender,
		getSubject,
		sendEmail
	} from './PlatformRequest.controller'
	import { t } from '$i18n/i18n'
	import ChipList from '../../organisms/ChipList.svelte'
	import AttachmentInfo from '../../molecules/AttachmentInfo.svelte'
	import Notifications from '../../organisms/Notifications.svelte'
	import { writable, type Writable } from 'svelte/store'
	import type { FormError } from '$types/formError'
	import { getFormErrors } from '$utils/formValidation'
	import { addNotification } from '#lib/stores.js'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { DefaultRobotoFontProps } from '$types/fontProps.js'
	import Button from '#lib/atoms/Button.svelte'
	import MapLoadingAnimation from '$mapapp/MapApp/MapView/MapLoadingAnimation.svelte'
	import type { ValidationError } from 'yup'

	const formErrors: Writable<FormError[]> = setContext(
		'formErrors',
		writable([{ path: '', message: '' }])
	)
	const FORM_ERROR_PATH_PREFIX: string = 'prm.'

	interface Props {
		lotId: string
		popId: string
	}
	let { lotId, popId }: Props = $props()

	let platformRequest: PlatformRequest = $state(NewPlatformRequest())
	let recipientsEmail: string[] = $state([])
	let lastSend: PlatformRequestLastSend | undefined = $state()
	let failedEntries: string[] = $state([])
	let submitting: boolean = $state(false)
	let platformRequestDialog: HTMLDialogElement | undefined = $state()

	export function openPlatformRequestModal() {
		platformRequestDialog?.showModal()
		initialize()
	}

	export function closePlatformRequestModal() {
		platformRequestDialog?.close()
		dispatchEvent(
			new CustomEvent('platformRequestClosed', {
				detail: {
					lotId: lotId,
					popId: popId
				}
			})
		)
	}

	async function fetchSender() {
		if (lotId) {
			platformRequest.sender = await getSender(lotId)
		}
	}

	async function sendPlatformRequest() {
		submitting = true
		clearErrors()
		failedEntries = []
		let finalRecipients: PlatformRequestReceiver[] = []
		recipientsEmail.forEach((email) => {
			let recipientUser = platformRequest.recipients.find((r) => r.email === email)
			if (recipientUser) {
				finalRecipients.push(recipientUser)
			} else {
				finalRecipients.push({
					email: email,
					type: PlatformRequestPopUserType.CONTACT
				})
			}
		})
		platformRequest.recipients = finalRecipients

		try {
			await platformRequestSchema.validate(platformRequest, {
				stripUnknown: true,
				abortEarly: false
			})
		} catch (e: unknown) {
			const err = e as ValidationError
			$formErrors = getFormErrors(e, FORM_ERROR_PATH_PREFIX)
			if (err.inner.length > 0) {
				failedEntries = err.inner
					.filter((error) => error.type === 'email')
					.map((error) => error.value)
				recipientsEmail = recipientsEmail // Trigger reactivity
			}
			submitting = false
			return
		}

		const sendResult: PlatformRequestSendResponse = await sendEmail(platformRequest, lotId, popId)
		submitting = false
		switch (sendResult.type) {
			case PlatformRequestSendErrorType.GENERAL:
				addNotification({
					...DefaultNotification($t('UI.PlatformRequest.error.general'), NotificationType.ERROR),
					target: 'platformRequest'
				})
				break
			case PlatformRequestSendErrorType.FILES_NOT_IDENTICAL:
				addNotification({
					...DefaultNotification(
						$t('UI.PlatformRequest.error.attachments'),
						NotificationType.ERROR
					),
					target: 'platformRequest'
				})
				break
			case PlatformRequestSendErrorType.EMAIL_SEND_ERROR: {
				let failedEmails = sendResult.emails?.join(', ')
				addNotification({
					...DefaultNotification(
						`${$t('UI.PlatformRequest.error.mailSent')} ${failedEmails}`,
						NotificationType.ERROR
					),
					target: 'platformRequest'
				})
				failedEntries = sendResult.emails ?? []
				recipientsEmail = recipientsEmail
				break
			}
			case PlatformRequestSendErrorType.SUCCESS:
				closePlatformRequestModal()
		}
	}

	function wasLastSendWithLink() {
		return lastSend && PFR_LAST_SEND_WITH_TYPES.includes(lastSend.type)
	}

	export function clearErrors() {
		$formErrors = [{ path: '', message: '' }]
	}

	async function initialize() {
		// Reset data
		clearErrors()
		platformRequest = NewPlatformRequest()

		// Fetch data for current lotId and popId
		const receiverP = getReceiver(popId)
		const subjectP = getSubject(lotId)
		const attachmentsP = getAttachments(lotId)
		const lastSendP = getLastSend(lotId, popId)

		// Start the template request as soon as recipients are available
		const contentP = receiverP.then((recipients) => getMailTemplate(lotId, recipients))

		// Apply recipients when ready (doesn't block others since receiverP is already in flight)
		platformRequest.recipients = await receiverP

		// filter(Boolean) is the short form of filter(item => Boolean(item)) which essentially filters falsy values
		/**
		 * the backend may list two contact persons sharing one email address — dedupe via
		 * Set (insertion order preserved, first occurrence wins) so each address renders as
		 * one chip and receives the platform request once
		 */
		recipientsEmail = [...new Set(platformRequest.recipients.map((r) => r.email).filter(Boolean))]

		// Resolve the remaining results in parallel
		const [subject, files, last, content] = await Promise.all([
			subjectP,
			attachmentsP,
			lastSendP,
			contentP
		])

		platformRequest.subject = subject
		/**
		 * the backend may list the same file twice; the template's each block below keys by
		 * file.id, so duplicates would crash it (each_key_duplicate) — keep the first entry per id
		 */
		platformRequest.files = files.filter(
			(file, index) => files.findIndex((f) => f.id === file.id) === index
		)
		lastSend = last
		platformRequest.content = content
	}

	onMount(() => {
		window.addEventListener('closePlatformRequest', closePlatformRequestModal)
		return window.removeEventListener('closePlatformRequest', closePlatformRequestModal)
	})
</script>

<dialog bind:this={platformRequestDialog} class="prm__dialog">
	<!-- Notifications from the base template on the map are not ideally positioned for this modal, so we include the
Notification component here -->
	<Notifications position="absolute" notificationWidth="80%" top="0" target="platformRequest" />
	<div class="prm__card">
		<div class="prm__header">
			<h1>{$t('UI.PlatformRequests.modal.header')}</h1>
			<Icon
				clickable
				on:click={closePlatformRequestModal}
				size={24}
				src={closeIcon}
				titleAttr={$t('UI.button.close')}
			/>
		</div>
		<div class="prm__meta-info">
			<div class="prm__sender">
				<h2>{$t('UI.PlatformRequests.modal.sender')}</h2>
				{#await fetchSender()}
					<Skeleton width={150} height={12}>
						<rect width="150" height="12" x="0" y="0" rx="2" ry="2" />
					</Skeleton>
				{:then}
					<div>
						<span class="prm__sender-name">
							{platformRequest?.sender?.firstname}
							{platformRequest?.sender?.lastname}
						</span>
						<span class="prm__sender-email">{platformRequest?.sender?.email}</span>
					</div>
				{:catch e}
					<span class="prm__sender-name prm__error-text"
						>{$t('UI.PlatformRequests.modal.error', { message: e?.message || '' })}</span
					>
				{/await}
			</div>
			<div class="prm__receiver">
				<h2>{$t('UI.PlatformRequests.modal.recipient')}</h2>
				<ChipList
					bind:list={recipientsEmail}
					{failedEntries}
					placeholder="kontakt@example.org"
					errorPath={FORM_ERROR_PATH_PREFIX + 'recipients.*'}
					chipFontProps={DefaultRobotoFontProps('0.75rem', '0.625rem', '#9d9d9d', 'white')}
					singleLineMode
					onInputFinished={clearErrors}
				/>
			</div>
			<div class="prm__subject">
				<h2>{$t('UI.PlatformRequests.modal.subject')}</h2>
				<InputField
					bind:value={platformRequest.subject}
					errorPath={FORM_ERROR_PATH_PREFIX + 'subject'}
					format={inputFieldFormats.FULL}
					placeholder={$t('UI.PlatformRequests.modal.subject.placeholder')}
					on:input={clearErrors}
				/>
			</div>
		</div>
		<div class="prm__message">
			<h2>{$t('UI.PlatformRequests.modal.message')}</h2>
			<TextArea
				bind:text={platformRequest.content}
				errorPath={FORM_ERROR_PATH_PREFIX + 'content'}
				height="12.5rem"
				labelGap="0"
				marginTop="0"
				maxHeight="12.5rem"
				resize="vertical"
				placeholder={$t('UI.PlatformRequests.modal.message.placeholder')}
				on:input={clearErrors}
			/>
			<div class="prm__message-info">
				<div class="prm__message-options">
					<div class="prm__message-link">
						<Switch
							bind:checked={platformRequest.includeLink}
							ariaLabel={$t('UI.PlatformRequests.modal.includeLink')}
						/>
						<span class="prm__message-link">{$t('UI.PlatformRequests.modal.includeLink')}</span>
					</div>
					<div class="prm__message-link">
						<Switch
							bind:checked={platformRequest.protocolToHubspot}
							ariaLabel={$t('UI.PlatformRequests.modal.protocolInHubspot')}
						/>
						<span class="prm__message-link"
							>{$t('UI.PlatformRequests.modal.protocolInHubspot')}</span
						>
					</div>
				</div>
				{#if lastSend && lastSend.timestamp}
					<span class="prm__message_last_request"
						>{$t('UI.PlatformRequests.modal.latestRequest.date')}
						{formatIsoDateString(lastSend.timestamp, {
							year: 'numeric',
							month: 'short',
							day: '2-digit'
						})}
						<span class="prm__message_last_request_link">
							{$t(
								'UI.PlatformRequests.modal.latestRequest.' +
									(wasLastSendWithLink() ? 'with' : 'without')
							)}
							{$t('UI.PlatformRequests.modal.latestRequest.link')}
						</span>
					</span>
				{/if}
			</div>
		</div>
		<div class="prm__attachments">
			<h2>{$t('UI.PlatformRequests.modal.attachment')}</h2>
			<div class="prm__attachments-container">
				{#if platformRequest?.files?.length === 0}
					<div class="prm__attachments-empty-container">
						<span class="prm__attachments_empty"
							>{$t('UI.PlatformRequests.modal.attachment.empty')}</span
						>
					</div>
				{/if}
				{#each platformRequest.files as file (file.id)}
					<AttachmentInfo
						filename={file.filename}
						date={formatIsoDateString(file.date)}
						size={file.size}
					/>
				{/each}
			</div>
		</div>
		<div class="prm__divider"></div>
		<div class="prm__buttons">
			<SecondaryButton
				label={$t('Button.cancel')}
				on:click={closePlatformRequestModal}
				rightIcon={ButtonIcons.CROSS}
				sizeAdaptingToText
			/>
			<Button
				variant="primary"
				titleAttr={$t('UI.PlatformRequests.modal.button.send')}
				disabled={submitting}
				onclick={sendPlatformRequest}
			>
				{#if submitting}
					<MapLoadingAnimation backgroundColor="none" label="Wird gesendet..." />
				{:else}
					{$t('UI.PlatformRequests.modal.button.send')}
					<IconSendMail />
				{/if}
			</Button>
		</div>
	</div>
</dialog>

<style lang="scss">
	@use '$styles/global';

	dialog.prm__dialog {
		margin: auto;
		border: none;
		width: 73.125rem;
		padding: 0;
		background: none;
		font-family: unset;
		font-style: unset;
		font-weight: unset;
		font-size: unset;
		line-height: unset;
		color: unset;

		/* Hide scrollbar */
		scrollbar-width: none; /* Firefox */
		-ms-overflow-style: none; /* legacy Edge/IE */
	}

	/* Hide scrollbar Chrome/Safari/Chromium */
	dialog.prm__::-webkit-scrollbar {
		width: 0;
		height: 0;
	}

	dialog.prm__::backdrop {
		background: rgba(0, 0, 0, 0.8);
	}

	.prm__card {
		@include global.flex-col($row-gap: 1.5rem, $alignment: flex-start);

		width: 100%;
		max-width: 73.125rem;
		background: white;
		padding: 2rem;
		border-radius: 0.5rem;
		border: 1px solid #d9d9d9; // TODO: Replace color with variable when styleguide is up-to-date
	}

	.prm__header {
		@include global.flex-row($justify: space-between);

		width: 100%;
	}

	.prm__meta-info {
		@include global.flex-col($row-gap: 1rem, $alignment: flex-start);

		width: 100%;
	}

	.prm__sender {
		@include global.flex-row($col-gap: 1.617rem, $justify: flex-start);

		& > div {
			@include global.flex-row($justify: flex-start);

			gap: 0.31rem;
			flex-wrap: wrap;
		}
	}

	.prm__receiver {
		@include global.flex-row($col-gap: 1rem, $justify: flex-start, $alignment: flex-start);

		width: 100%;

		& > h2 {
			line-height: 2rem;
		}
	}

	.prm__sender-name,
	.prm__sender-email {
		@include global.roboto-font(0.625rem, 400, 0.75rem, #616161);
	}

	.prm__error-text {
		color: global.$primaryRed;
	}

	.prm__sender-email {
		border-radius: 1.1875rem;
		border: 1px solid var(--color-gray-400);
		padding: 0.125rem 0.375rem;
	}

	.prm__subject,
	.prm__message,
	.prm__attachments {
		@include global.flex-col($row-gap: 1rem, $alignment: flex-start);

		width: 100%;
	}

	.prm__attachments-container {
		@include global.flex-row($justify: flex-start);

		flex-wrap: wrap;
		gap: 0.94rem;
		background: global.$secondaryLightGreen;
		border: 1px dashed global.$primaryColor;
		border-radius: 0.25rem;
		padding: 0.75rem;
		width: 100%;

		&:has(:global(> .prm__attachments-empty-container)) {
			justify-content: center;
		}

		.prm__attachments-empty-container {
			background: white;
			padding: 0.25rem 0.625rem;
			border-radius: 0.125rem;

			.prm__attachments_empty {
				@include global.roboto-font(normal, 400, 0.875rem);
			}
		}
	}

	.prm__message-info {
		@include global.flex-row($justify: space-between);

		flex-wrap: wrap;
		gap: 0.5rem;
		width: 100%;
	}

	.prm__message-link {
		@include global.flex-row($col-gap: 0.5rem);
	}

	.prm__message-options {
		@include global.flex-row($col-gap: 1.5rem, $justify: flex-start);

		flex-wrap: wrap;
	}

	.prm__message_last_request {
		@include global.roboto-font(1.125rem, 400, 0.75rem);
	}

	.prm__message_last_request_link {
		font-weight: 700;
	}

	.prm__divider {
		width: 100%;
		height: 0.0625rem;
		background: #d9d9d9;
	}

	.prm__buttons {
		@include global.flex-row($col-gap: 1rem, $justify: space-between);

		gap: 1rem;
		flex-wrap: wrap;
		width: 100%;
		margin-top: -0.5rem;
	}

	@media (max-width: global.$small) {
		.prm__card {
			padding: 1.5rem;
		}
	}

	/* FIXME: Remove when we got rid of Sirius/wondergem.css since it adds some weird stylings to our h1 tags...I hate Sirius. */
	h1,
	h2 {
		display: unset;
		width: unset;
		padding: unset;
		border: unset;
		margin: unset;
	}
</style>
